import { Popover } from '@headlessui/react';
import { Icon, Input } from 'lynkco-up-core';
import React, { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import dayjs from 'dayjs';
import { vehicleModel } from '../shared/utils';
import { useUserContext } from '../../modules/Users/UserContext';
import { GetColab } from '../../modules/Colab/types';
import { FilterOption, getCategoryOptions, getUniqueOptions, recordStatuses } from './colabOptions';
import ColabCheckbox from './colabCheckbox';

type ColabFilterProp = {
  updateFilters: (
    startDate: string,
    endDate: string,
    vehicleModel: string[],
    status: string[],
    category: string[],
    recordType: string,
    source: string[],
    market: string[],
    minAge: string,
    maxAge: string,
    searchText: string,
  ) => void;
  colabRecords: GetColab[];
  setPerPage: React.Dispatch<React.SetStateAction<number>>;
  setDisplayAll: React.Dispatch<React.SetStateAction<boolean>>;
  setHasFilters: React.Dispatch<React.SetStateAction<boolean>>;
  actions?: React.ReactNode;
};

const toggleFilterPanelKey = 'e';
const modifierKeySymbol = navigator.userAgent.includes('Mac') ? '⌘' : 'Ctrl';
const toggleFilterPanelShortcutSymbol = modifierKeySymbol + '+' + toggleFilterPanelKey.toUpperCase();

const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

export const recordType = [
  { id: 'All', label: 'All records', value: '' },
  { id: 'EmailNotProvided', label: 'Anonymous records', value: 'EmailNotProvided' },
  { id: 'EmailProvided', label: 'Non-anonymous records', value: 'EmailProvided' },
];

type MultiSelectFilterGroupProps = {
  title: string;
  allLabel: string;
  emptyText: string;
  idPrefix: string;
  isAllSelected: boolean;
  onChange: (event: React.FormEvent<HTMLInputElement>) => void;
  options: FilterOption[];
  selectedValues: string[];
};

type FilterChipProps = {
  label: string;
  onRemove: () => void;
};

const inputClasses =
  'block h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-100';

function classNames(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

function getSelectedCount(selectedValues: string[], allValues: string[]) {
  if (selectedValues.length === 0 || selectedValues.length === allValues.length) {
    return 0;
  }

  return selectedValues.length;
}

function FilterChip({ label, onRemove }: FilterChipProps) {
  return (
    <span className="m-1 inline-flex items-center rounded-full bg-gray-100 py-1.5 pr-2 pl-3 text-sm font-medium text-gray-900">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
        aria-label={`Remove ${label} filter`}>
        <svg fill="none" stroke="currentColor" viewBox="0 0 8 8" className="h-2.5 w-2.5" aria-hidden="true">
          <path d="M1 1l6 6m0-6L1 7" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </span>
  );
}

type FilterPopoverProps = {
  children: React.ReactNode;
  count?: number;
  label: string;
  panelClasses?: string;
};

function FilterPopover({ children, count = 0, label, panelClasses }: FilterPopoverProps) {
  return (
    <Popover className="colab-filter-popover relative mr-1.5 inline-block text-left">
      {({ open }) => (
        <>
          <Popover.Button
            className={classNames(
              'colab-filter-popover__button group inline-flex h-10 items-center justify-center rounded-md pl-1.5 pr-0.5 text-sm font-normal text-gray-700 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2',
              open && 'bg-gray-50 text-gray-900',
            )}>
            <span>{label}</span>
            {count > 0 && (
              <span className="ml-1.5 rounded-sm bg-gray-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                {count}
              </span>
            )}
            <span
              className={classNames(
                'colab-filter-popover__icon ml-0.5 flex text-gray-400 transition-transform',
                open && 'rotate-180',
              )}>
              <Icon name="keyboard_arrow_down" size="small" variant="gray" padding={0} />
            </span>
          </Popover.Button>

          <Popover.Panel
            className={classNames(
              'absolute right-0 z-20 mt-2 rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5 focus:outline-none',
              panelClasses || 'w-72',
            )}>
            {children}
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
}

function MultiSelectFilterGroup({
  title,
  allLabel,
  emptyText,
  idPrefix,
  isAllSelected,
  onChange,
  options,
  selectedValues,
}: MultiSelectFilterGroupProps) {
  const hasOptions = options.length > 0;

  return (
    <fieldset>
      <legend className="sr-only">{title}</legend>
      {hasOptions ? (
        <div className="space-y-4">
          <div className="border-b border-gray-100 pb-3">
            <ColabCheckbox
              id={`${idPrefix}-all`}
              label={allLabel}
              value="ALL"
              onChange={onChange}
              checked={isAllSelected}
            />
          </div>
          <div className="max-h-64 space-y-3 overflow-y-auto pr-2">
            {options.map(({ id, label, value }) => (
              <div key={`${idPrefix}-${id}`}>
                <ColabCheckbox
                  id={`${idPrefix}-${id}`}
                  label={label}
                  value={value}
                  onChange={onChange}
                  checked={selectedValues.includes(value)}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">{emptyText}</p>
      )}
    </fieldset>
  );
}

function ColabFilters({
  updateFilters,
  colabRecords,
  setPerPage,
  setDisplayAll,
  setHasFilters,
  actions,
}: ColabFilterProp) {
  const { checkAccess } = useUserContext();
  const hasAccess = (scope: string) => checkAccess(scope);
  const [startingDate, setStartingDate] = useState('');
  const [endingDate, setEndingDate] = useState('');
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [selectedRecordType, setSelectedRecordType] = useState('');
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  const modelOptions = vehicleModel.filter(model => model.value !== '');
  const allModelValues = modelOptions.map(item => item.value);
  const isAllModelsSelected = allModelValues.length > 0 && selectedModels.length === allModelValues.length;

  const allStatusValues = recordStatuses.map(item => item.value);
  const isAllStatusSelected = allStatusValues.length > 0 && selectedStatus.length === allStatusValues.length;

  const recordCategories = getCategoryOptions();
  const allCategoryValues = recordCategories.map(item => item.value);
  const isAllCategorySelected = allCategoryValues.length > 0 && selectedCategory.length === allCategoryValues.length;

  const sourceOptions = getUniqueOptions(colabRecords.map(record => record.fromAppName));
  const allSourceValues = sourceOptions.map(item => item.value);
  const isAllSourcesSelected = allSourceValues.length > 0 && selectedSources.length === allSourceValues.length;

  const marketOptions = getUniqueOptions(colabRecords.map(record => record.vehicleMarket));
  const allMarketValues = marketOptions.map(item => item.value);
  const isAllMarketsSelected = allMarketValues.length > 0 && selectedMarkets.length === allMarketValues.length;
  const hasModelFilter = selectedModels.length > 0 && !isAllModelsSelected;
  const hasStatusFilter = selectedStatus.length > 0 && !isAllStatusSelected;
  const hasCategoryFilter = selectedCategory.length > 0 && !isAllCategorySelected;
  const hasSourceFilter = selectedSources.length > 0 && !isAllSourcesSelected;
  const hasMarketFilter = selectedMarkets.length > 0 && !isAllMarketsSelected;
  const hasDateFilter = startingDate !== '' || endingDate !== '';
  const hasSearchFilter = searchText.trim() !== '';
  const hasRecordTypeFilter = selectedRecordType !== '';
  const modelFilterCount = getSelectedCount(selectedModels, allModelValues);
  const statusFilterCount = getSelectedCount(selectedStatus, allStatusValues);
  const categoryFilterCount = getSelectedCount(selectedCategory, allCategoryValues);
  const sourceFilterCount = getSelectedCount(selectedSources, allSourceValues);
  const marketFilterCount = getSelectedCount(selectedMarkets, allMarketValues);
  const dateFilterCount = hasDateFilter ? 1 : 0;
  const recordTypeFilterCount = hasRecordTypeFilter ? 1 : 0;
  const activeFilterChips = [
    { isActive: hasDateFilter, label: 'Date', onRemove: () => clearDateFilters() },
    { isActive: hasModelFilter, label: `Model (${selectedModels.length})`, onRemove: () => setSelectedModels([]) },
    { isActive: hasStatusFilter, label: `Status (${selectedStatus.length})`, onRemove: () => setSelectedStatus([]) },
    {
      isActive: hasCategoryFilter,
      label: `Category (${selectedCategory.length})`,
      onRemove: () => setSelectedCategory([]),
    },
    { isActive: hasRecordTypeFilter, label: 'Record type', onRemove: () => setSelectedRecordType('') },
    { isActive: hasSourceFilter, label: `Source (${selectedSources.length})`, onRemove: () => setSelectedSources([]) },
    { isActive: hasMarketFilter, label: `Market (${selectedMarkets.length})`, onRemove: () => setSelectedMarkets([]) },
    { isActive: hasSearchFilter, label: 'Search', onRemove: () => setSearchText('') },
  ].filter(chip => chip.isActive);
  const numberOfActiveFilters = activeFilterChips.length;

  function clearDateFilters() {
    setStartingDate('');
    setEndingDate('');
  }

  function handleSetFilters() {
    const modifiedFormatStartDate = dayjs(startingDate).startOf('day').format(dateFormat);
    const modifiedFormatEndDate = dayjs(endingDate).endOf('day').format(dateFormat);
    updateFilters(
      startingDate !== '' ? modifiedFormatStartDate : startingDate,
      endingDate !== '' ? modifiedFormatEndDate : endingDate,
      selectedModels.length === 0 || selectedModels.length === modelOptions.length ? [] : selectedModels,
      selectedStatus.length === 0 || selectedStatus.length === allStatusValues.length ? [] : selectedStatus,
      selectedCategory.length === 0 || selectedCategory.length === allCategoryValues.length ? [] : selectedCategory,
      selectedRecordType,
      selectedSources.length === 0 || selectedSources.length === allSourceValues.length ? [] : selectedSources,
      selectedMarkets.length === 0 || selectedMarkets.length === allMarketValues.length ? [] : selectedMarkets,
      '',
      '',
      searchText.trim(),
    );
    setHasFilters(numberOfActiveFilters > 0);
    setDisplayAll(false);
    setPerPage(20);
  }

  useHotkeys(toggleFilterPanelShortcutSymbol, () => {
    document.getElementById('searchText')?.focus();
  });

  function handleChange(event: React.FormEvent<HTMLInputElement>) {
    const { value, id, type } = event.currentTarget;
    if (id === 'searchText') {
      setSearchText(value);
    } else if (type === 'checkbox') {
      handleModelChange(event);
    } else {
      id === 'startDate' ? setStartingDate(value) : setEndingDate(value);
    }
  }

  function handleModelChange(event: React.FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    if (value === 'ALL') {
      setSelectedModels(isAllModelsSelected ? [] : allModelValues);
    } else {
      const updated = selectedModels.includes(value)
        ? selectedModels.filter(v => v !== value)
        : [...selectedModels, value];

      setSelectedModels(updated);
    }
  }

  function handleStatusChange(event: React.FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    if (value === 'ALL') {
      setSelectedStatus(isAllStatusSelected ? [] : allStatusValues);
    } else {
      const updated = selectedStatus.includes(value)
        ? selectedStatus.filter(v => v !== value)
        : [...selectedStatus, value];

      setSelectedStatus(updated);
    }
  }

  function handleCategoryChange(event: React.FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    if (value === 'ALL') {
      setSelectedCategory(isAllCategorySelected ? [] : allCategoryValues);
    } else {
      const updated = selectedCategory.includes(value)
        ? selectedCategory.filter(v => v !== value)
        : [...selectedCategory, value];

      setSelectedCategory(updated);
    }
  }

  function handleSourceChange(event: React.FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    if (value === 'ALL') {
      setSelectedSources(isAllSourcesSelected ? [] : allSourceValues);
    } else {
      const updated = selectedSources.includes(value)
        ? selectedSources.filter(v => v !== value)
        : [...selectedSources, value];

      setSelectedSources(updated);
    }
  }

  function handleMarketChange(event: React.FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    if (value === 'ALL') {
      setSelectedMarkets(isAllMarketsSelected ? [] : allMarketValues);
    } else {
      const updated = selectedMarkets.includes(value)
        ? selectedMarkets.filter(v => v !== value)
        : [...selectedMarkets, value];

      setSelectedMarkets(updated);
    }
  }

  function handleRecordTypeChange(event: React.FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    setSelectedRecordType(value);
  }

  function handleClearFilters() {
    const defaultStart = dayjs().subtract(100, 'day').startOf('day').format(dateFormat);
    const defaultEnd = dayjs().endOf('day').format(dateFormat);
    updateFilters(defaultStart, defaultEnd, [], [], [], '', [], [], '', '', '');
    setStartingDate('');
    setEndingDate('');
    setSelectedModels([]);
    setSelectedStatus([]);
    setSelectedCategory([]);
    setSelectedRecordType('');
    setSelectedSources([]);
    setSelectedMarkets([]);
    setSearchText('');
    setDisplayAll(false);
    setPerPage(20);
    setHasFilters(false);
  }

  return (
    <section aria-labelledby="colab-filter-heading" className="bg-white colab-filters">
      <h2 id="colab-filter-heading" className="sr-only">
        Filters
      </h2>

      <div className="px-6">
        <div className="colab-filters__controls flex flex-wrap items-center gap-x-2 gap-y-2 py-3">
          <Input
            type="search"
            id="searchText"
            value={searchText}
            onChange={handleChange}
            placeholder="Search feedback, VIN..."
            leadingIcon="search"
            extraClasses="colab-filters__search w-64 max-w-full pr-3"
          />

          <FilterPopover label="Date" count={dateFilterCount}>
            <div className="space-y-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                  Start date
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={startingDate}
                  onChange={handleChange}
                  className={`${inputClasses} mt-1`}
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                  End date
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={endingDate}
                  onChange={handleChange}
                  className={`${inputClasses} mt-1`}
                />
              </div>
            </div>
          </FilterPopover>

          <FilterPopover label="Model" count={modelFilterCount}>
            <MultiSelectFilterGroup
              title="Vehicle model"
              allLabel="All models"
              emptyText="No models available"
              idPrefix="model"
              isAllSelected={isAllModelsSelected}
              onChange={handleModelChange}
              options={modelOptions}
              selectedValues={selectedModels}
            />
          </FilterPopover>

          <FilterPopover label="Status" count={statusFilterCount}>
            <MultiSelectFilterGroup
              title="Status"
              allLabel="All statuses"
              emptyText="No statuses available"
              idPrefix="status"
              isAllSelected={isAllStatusSelected}
              onChange={handleStatusChange}
              options={recordStatuses}
              selectedValues={selectedStatus}
            />
          </FilterPopover>

          <FilterPopover label="Category" count={categoryFilterCount} panelClasses="w-80">
            <MultiSelectFilterGroup
              title="Category"
              allLabel="All categories"
              emptyText="No categories available"
              idPrefix="category"
              isAllSelected={isAllCategorySelected}
              onChange={handleCategoryChange}
              options={recordCategories}
              selectedValues={selectedCategory}
            />
          </FilterPopover>

          <FilterPopover label="Source" count={sourceFilterCount} panelClasses="w-96">
            <MultiSelectFilterGroup
              title="Source"
              allLabel="All sources"
              emptyText="No sources available"
              idPrefix="source"
              isAllSelected={isAllSourcesSelected}
              onChange={handleSourceChange}
              options={sourceOptions}
              selectedValues={selectedSources}
            />
          </FilterPopover>

          <FilterPopover label="Market" count={marketFilterCount}>
            <MultiSelectFilterGroup
              title="Market"
              allLabel="All markets"
              emptyText="No markets available"
              idPrefix="market"
              isAllSelected={isAllMarketsSelected}
              onChange={handleMarketChange}
              options={marketOptions}
              selectedValues={selectedMarkets}
            />
          </FilterPopover>

          {(hasAccess('COLAB_FULL_ACCESS') || hasAccess('COLAB_ADMIN')) && (
            <FilterPopover label="Record type" count={recordTypeFilterCount}>
              <fieldset>
                <legend className="sr-only">Record type</legend>
                <div className="space-y-3">
                  {recordType.map(({ id, label, value }) => {
                    return (
                      <div key={id} className="flex items-center">
                        <input
                          id={`record-type-${id}`}
                          type="radio"
                          name="recordType"
                          value={value}
                          checked={value === selectedRecordType}
                          onChange={handleRecordTypeChange}
                          className="h-4 w-4 cursor-pointer border-gray-300 accent-teal-700 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
                        />
                        <label
                          htmlFor={`record-type-${id}`}
                          className="ml-3 block cursor-pointer text-base font-normal leading-6 text-gray-700">
                          {label}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>
            </FilterPopover>
          )}

          <span className="ml-auto" aria-hidden="true" />
          <button
            type="button"
            onClick={handleSetFilters}
            className="inline-flex h-10 items-center rounded-md bg-primary-500 px-3 text-sm font-semibold text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2">
            Apply
            <span className="ml-2 flex items-center rounded">
              <Icon name="search" size="small" variant="light" padding={0} />
            </span>
          </button>
          {numberOfActiveFilters > 0 && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="inline-flex h-10 items-center rounded-md px-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2">
              Clear
            </button>
          )}
          <span className="hidden text-xs font-semibold text-gray-400 sm:inline-flex">
            {toggleFilterPanelShortcutSymbol}
          </span>
          {actions && <div className="flex items-center">{actions}</div>}
        </div>

        {activeFilterChips.length > 0 && (
          <div className="-mx-1 pb-2">
            <div className="-m-1 flex flex-wrap items-center">
              <span className="m-1 text-sm font-medium text-gray-500">Filters</span>
              {activeFilterChips.map(({ label, onRemove }) => (
                <FilterChip key={label} label={label} onRemove={onRemove} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default ColabFilters;
